import { Pattern } from '../app/builder/contexts/PatternContext'
import { Alert } from '../contexts/ApplicationSuiteProvider'
import { getClientTimeZone } from '../common/timezones'

/**
 * Tutorial Mock Data
 *
 * This file contains mock data used exclusively for tutorial demonstrations.
 * The mock patterns and alerts created here are only shown to users when they are actively
 * going through a tutorial walkthrough, providing them with sample data to
 * interact with during the learning process.
 *
 * Mock data is separated from real application data to:
 * - Keep tutorial content consistent and predictable
 * - Avoid cluttering the user's real data
 * - Provide a controlled learning environment
 * - Allow easy updates to tutorial content without affecting user patterns
 */
export const createTutorialMockPattern = (tradingHouse: string): Pattern => ({
  id: 'tutorial-demo-pattern',
  name: 'Tutorial Demo Pattern',
  buyConditionGroups: [
    {
      id: 'tutorial-buy-group-1',
      type: 'group',
      groupTitle: 'Entry Conditions',
      count: 1,
      symbol: 'EURUSD',
      periodicity: 1,
      timeframe: 'minute',
      depth: 1,
      conditions: [
        {
          id: 'tutorial-condition-1',
          type: 'condition',
          chartDetails: {
            symbol: 'EURUSD',
            periodicity: 1,
            timeframe: 'minute',
          },
          condition: {
            LHS: {
              field: 'value',
              indicator: { name: 'close' },
            },
            conditionType: 'greater than',
            RHS: {
              field: 'value',
              value: '150',
              indicator: { name: 'value' },
            },
            isValid: true,
          },
          sourceGroupId: 'tutorial-buy-group-1',
        },
      ],
      conditionGroups: [],
    },
  ],
  sellConditionGroups: [
    {
      id: 'tutorial-sell-group-1',
      type: 'group',
      groupTitle: 'Exit Conditions',
      count: 1,
      symbol: 'EURUSD',
      periodicity: 1,
      timeframe: 'minute',
      depth: 1,
      conditions: [
        {
          id: 'tutorial-condition-2',
          type: 'condition',
          chartDetails: {
            symbol: 'EURUSD',
            periodicity: 1,
            timeframe: 'minute',
          },
          condition: {
            LHS: {
              field: 'value',
              indicator: { name: 'close' },
            },
            conditionType: 'less than',
            RHS: {
              field: 'value',
              value: '140',
              indicator: { name: 'value' },
            },
            isValid: true,
          },
          sourceGroupId: 'tutorial-sell-group-1',
        },
      ],
      conditionGroups: [],
    },
  ],
  active: true,
  version: 1,
  alertVersion: 1,
  tradingHouse,
  trigger: {
    type: 'atPost',
    chartDetails: {
      symbol: 'EURUSD',
      periodicity: 1,
      timeframe: 'minute',
    },
  },
  author: 'Tutorial System',
  message: 'This is a demonstration pattern used during the tutorial walkthrough',
})

export const createTutorialMockAlert = (usermeta: any, tradingHouse: string): Alert => ({
  id: 'tutorial-demo-alert',
  name: 'Tutorial Demo Alert',
  pattern: createTutorialMockPattern(tradingHouse),
  symbol: 'EURUSD',
  timeRanges: [
    {
      startTimeRange: '09:30',
      endTimeRange: '16:00',
      key: 1,
      allowableCount: 3,
    },
  ],
  timezone: getClientTimeZone(),
  daysOfWeek: [1, 2, 3, 4, 5],
  alertTypes: ['email', 'app'],
  user: {
    gcid: usermeta?.gcid || 'tutorial-user',
    email: usermeta?.email || 'tutorial@example.com',
    phoneNumber: '',
  },
  active: true,
  from: 'pattern',
})
